import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import DateRangePicker from '../components/ui/date-range-picker';
import GstFilingPreviewPanel from '../components/admin/GstFilingPreviewPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { 
  TrendingUp, TrendingDown, Receipt, DollarSign, 
  Users, FileText, Download, AlertCircle, Plus 
} from 'lucide-react';

const AdminTaxDashboard = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [gstr8Reports, setGstr8Reports] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  // iter-251 — Universal date range filter. Defaults to current Financial Year.
  // Picker handles URL + localStorage persistence internally.
  const [dateRange, setDateRange] = useState(null);

  const fetchData = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString(),
      }).toString();
      const [analyticsRes, vendorsRes, reportsRes] = await Promise.all([
        apiCall(`/api/tax-management/admin/analytics?${params}`),
        apiCall(`/api/tax-management/admin/vendors-summary?${params}`),
        apiCall('/api/tax-management/admin/gstr8-reports')
      ]);

      if (analyticsRes.success) setAnalytics(analyticsRes.analytics);
      if (vendorsRes.success) setVendors(vendorsRes.vendors);
      if (reportsRes.success) setGstr8Reports(reportsRes.reports);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message, 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // iter-251 — Client-side CSV export. Uses the already-loaded
  // analytics + vendors data and the active date-range label so the
  // exported file matches exactly what's on screen. No extra backend
  // endpoint needed.
  const downloadCsv = useCallback(() => {
    if (!analytics || !dateRange?.from || !dateRange?.to) return;
    const fmt = (v) => v == null ? '' : String(v);
    const numFmt = (v) => v == null || v === '' ? '0.00' : Number(v).toFixed(2);
    const rangeLabel = `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`;
    const sum = analytics.summary || {};

    const lines = [];
    lines.push(`Tax Management Report,Period: ${format(dateRange.from, 'd MMM yyyy')} - ${format(dateRange.to, 'd MMM yyyy')}`);
    lines.push(`Financial Year,${analytics.financial_year || ''}`);
    lines.push('');
    lines.push('# Summary');
    lines.push('Metric,Value (INR)');
    lines.push(`Total Gross Sales,${numFmt(sum.total_gross_sales)}`);
    lines.push(`Total TDS Collected,${numFmt(sum.total_tds_collected)}`);
    lines.push(`Total TCS Collected,${numFmt(sum.total_tcs_collected)}`);
    lines.push(`Total Commission,${numFmt(sum.total_commission)}`);
    lines.push(`Commission GST (18%),${numFmt(sum.commission_gst)}`);
    lines.push(`Total Revenue,${numFmt(sum.total_revenue)}`);
    lines.push(`Average Transaction,${numFmt(sum.average_transaction)}`);
    lines.push(`Transaction Count,${fmt(sum.transaction_count)}`);
    lines.push('');
    lines.push('# Vendor Breakdown');
    lines.push('Vendor Name,Vendor Email,GSTIN,Gross Sales,TDS,TCS,Commission,Commission GST,Net Payout,Txn Count');
    (vendors || []).forEach((v) => {
      lines.push([
        v.vendor_name, v.vendor_email, v.gstin || '',
        numFmt(v.gross_sales), numFmt(v.tds_collected), numFmt(v.tcs_collected),
        numFmt(v.commission), numFmt(v.commission_gst), numFmt(v.net_payout),
        fmt(v.transaction_count),
      ].map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax-report_${rangeLabel}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: 'CSV downloaded', description: `tax-report_${rangeLabel}.csv` });
  }, [analytics, vendors, dateRange, toast]);

  const generateGSTR8 = async () => {
    const now = new Date();
    const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

    try {
      const response = await apiCall('/api/tax-management/generate-gstr8', {
        method: 'POST',
        body: JSON.stringify({
          month: lastMonth,
          year: year
        })
      });

      if (response.success) {
        toast({ title: 'Success', description: 'GSTR-8 report generated successfully' });
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Tax Management</h1>
              <p className="text-gray-600 mt-1">Loading…</p>
            </div>
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              storageKey="taxManagement"
              defaultPreset="current_fy"
              testid="tax-management-date-range"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4].map(i => (
              <Card key={`tax-skeleton-${i}`}>
                <CardContent className="p-6">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-3" />
                  <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const summary = analytics?.summary || {};
  const stats = [
    {
      title: 'Total Gross Sales',
      value: `₹${summary.total_gross_sales?.toLocaleString('en-IN') || '0'}`,
      icon: DollarSign,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Total TDS Collected',
      value: `₹${summary.total_tds_collected?.toLocaleString('en-IN') || '0'}`,
      icon: TrendingDown,
      color: 'text-red-600',
      bg: 'bg-red-50'
    },
    {
      title: 'Total TCS Collected',
      value: `₹${summary.total_tcs_collected?.toLocaleString('en-IN') || '0'}`,
      icon: Receipt,
      color: 'text-orange-600',
      bg: 'bg-orange-50'
    },
    {
      title: 'Active Vendors',
      value: summary.unique_vendors || '0',
      icon: Users,
      color: 'text-green-600',
      bg: 'bg-green-50'
    }
  ];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tax Management</h1>
            <p className="text-gray-600 mt-1">
              {analytics?.period?.start && analytics?.period?.end ? (
                <>Period: {format(new Date(analytics.period.start), 'd MMM yyyy')} – {format(new Date(analytics.period.end), 'd MMM yyyy')}</>
              ) : (
                <>Financial Year {analytics?.financial_year}</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              storageKey="taxManagement"
              defaultPreset="current_fy"
              testid="tax-management-date-range"
            />
            <Button
              variant="outline"
              onClick={downloadCsv}
              disabled={!dateRange?.from || !dateRange?.to}
              data-testid="tax-download-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
            <Button onClick={generateGSTR8} data-testid="tax-generate-gstr8-btn">
              <Plus className="w-4 h-4 mr-2" />
              Generate GSTR-8
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('vendors')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'vendors'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vendors
            </button>
            <button
              onClick={() => setActiveTab('gstr8')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'gstr8'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              GSTR-8 Reports
            </button>
            {/* iter-252e PR 7 — GST Filing Preview tab (D-034 SSOT) */}
            <button
              onClick={() => setActiveTab('gst_filing')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'gst_filing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tax-tab-gst-filing"
            >
              GST Filing Preview
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <Card key={stat.id || stat.name || `${stat.title || "stat"}-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                        <p className="text-2xl font-bold mt-2">{stat.value}</p>
                      </div>
                      <div className={`${stat.bg} p-3 rounded-lg`}>
                        <stat.icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Commission Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Total Commission</span>
                      <span className="font-bold">
                        ₹{summary.total_commission?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Commission GST (18%)</span>
                      <span className="font-bold">
                        ₹{summary.total_commission_gst?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                      <span className="text-sm font-medium text-green-700">Total Revenue</span>
                      <span className="font-bold text-green-700">
                        ₹{((summary.total_commission || 0) + (summary.total_commission_gst || 0)).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Transaction Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Total Transactions</span>
                      <span className="font-bold">{summary.total_transactions || '0'}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Total Payout to Vendors</span>
                      <span className="font-bold">
                        ₹{summary.total_net_payout?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Average Transaction</span>
                      <span className="font-bold">
                        ₹{summary.total_transactions > 0 
                          ? Math.round(summary.total_gross_sales / summary.total_transactions).toLocaleString('en-IN')
                          : '0'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-start">
                  <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Tax Compliance Notes</h3>
                    <ul className="space-y-2 text-sm text-blue-700">
                      <li>• <strong>GSTR-8:</strong> Must be filed by 10th of following month</li>
                      <li>• <strong>TDS:</strong> Deducted under Section 194-O at 0.1%</li>
                      <li>• <strong>TCS:</strong> Collected at 1% on net taxable value</li>
                      <li>• <strong>Reporting:</strong> Maintain all tax records for audit</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Vendors Tab */}
        {activeTab === 'vendors' && (
          <Card>
            <CardHeader>
              <CardTitle>Vendor-wise Tax Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Vendor</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">PAN</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Transactions</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Gross Sales</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">TDS</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">TCS</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Net Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((vendor, index) => (
                      <tr key={vendor.id || vendor.name || `${vendor.title || "vendor"}-${index}`} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">
                          <div className="font-medium">{vendor.vendor_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{vendor.vendor_id}</div>
                        </td>
                        <td className="p-3 text-sm font-mono">{vendor.vendor_pan || 'N/A'}</td>
                        <td className="p-3 text-sm text-right">{vendor.transactions}</td>
                        <td className="p-3 text-sm text-right">
                          ₹{vendor.gross_sales?.toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-sm text-right text-red-600">
                          ₹{vendor.tds_deducted?.toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-sm text-right text-orange-600">
                          ₹{vendor.tcs_collected?.toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-sm text-right font-semibold text-green-600">
                          ₹{vendor.net_payout?.toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* GSTR-8 Tab */}
        {activeTab === 'gstr8' && (
          <Card>
            <CardHeader>
              <CardTitle>GSTR-8 Monthly Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {gstr8Reports.length > 0 ? (
                  gstr8Reports.map((report, index) => (
                    <div key={report.id || report.name || `${report.title || "report"}-${index}`} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-blue-600 mr-2" />
                            <h3 className="font-semibold">
                              {report.month} {report.year}
                            </h3>
                            <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                              report.filing_status === 'filed' 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {report.filing_status}
                            </span>
                          </div>
                          <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                            <div>
                              <span className="text-gray-600">Total TCS:</span>
                              <div className="font-semibold">
                                ₹{report.total_tcs_collected?.toLocaleString('en-IN')}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-600">Vendors:</span>
                              <div className="font-semibold">{report.number_of_vendors}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Transactions:</span>
                              <div className="font-semibold">{report.number_of_transactions}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Total Supplies:</span>
                              <div className="font-semibold">
                                ₹{report.total_supplies?.toLocaleString('en-IN')}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          <Button size="sm" variant="outline">
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No GSTR-8 reports generated yet</p>
                    <Button onClick={generateGSTR8} className="mt-4">
                      Generate First Report
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter-252e PR 7 — GST Filing Preview tab (read-only, D-034 SSOT) */}
        {activeTab === 'gst_filing' && (
          <div data-testid="tax-tab-gst-filing-content">
            <GstFilingPreviewPanel />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminTaxDashboard;

